import React from 'react';
import { Building, Stethoscope, Scissors, Scale, Utensils, Car, GraduationCap, ShoppingBag, Plane, Home, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Navbar from './Navbar';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';

const UseCasesPage = () => {
  const { login } = useAuth();

  const industries = [
    {
      icon: Building,
      name: 'Imobiliárias',
      description: 'Automatize visitas, qualifique leads e agende reuniões com compradores.',
      benefits: ['25% mais agendamentos', 'Qualificação automática', 'Follow-up personalizado'],
      color: 'bg-blue-500'
    },
    {
      icon: Stethoscope,
      name: 'Clínicas Médicas',
      description: 'Agendamento de consultas, lembretes e pré-atendimento automatizado.',
      benefits: ['30% menos faltas', 'Triagem automática', 'Confirmação por WhatsApp'],
      color: 'bg-red-500'
    },
    {
      icon: Scissors,
      name: 'Salões de Beleza',
      description: 'Marque horários, envie lembretes e fidelize clientes automaticamente.',
      benefits: ['40% mais retorno', 'Agenda otimizada', 'Promoções segmentadas'],
      color: 'bg-pink-500'
    },
    {
      icon: Scale,
      name: 'Escritórios de Advocacia',
      description: 'Capture leads, qualifique casos e agende consultas iniciais.',
      benefits: ['Triagem de casos', 'Documentação digital', 'Acompanhamento processual'],
      color: 'bg-gray-700'
    },
    {
      icon: Utensils,
      name: 'Restaurantes e Cafés',
      description: 'Pedidos pelo WhatsApp, reservas de mesa e programa de fidelidade.',
      benefits: ['Cardápio digital', 'Pedidos 24/7', 'Delivery integrado'],
      color: 'bg-orange-500'
    },
    {
      icon: ShoppingBag,
      name: 'E-commerce',
      description: 'Recupere carrinhos abandonados, suporte ao cliente e rastreamento.',
      benefits: ['15% mais conversão', 'Suporte instantâneo', 'Recomendações IA'],
      color: 'bg-purple-500'
    },
    {
      icon: Car,
      name: 'Concessionárias',
      description: 'Test drives, cotações e acompanhamento pós-venda automatizado.',
      benefits: ['Agendamento fácil', 'Catálogo interativo', 'Financiamento'],
      color: 'bg-gray-600'
    },
    {
      icon: GraduationCap,
      name: 'Instituições de Ensino',
      description: 'Matrículas, informações de cursos e suporte ao aluno.',
      benefits: ['Processo simplificado', 'FAQ automático', 'Lembretes de pagamento'],
      color: 'bg-green-600'
    },
    {
      icon: Plane,
      name: 'Turismo e Viagens',
      description: 'Cotações, reservas e suporte durante a viagem.',
      benefits: ['Pacotes personalizados', 'Suporte 24/7', 'Check-in digital'],
      color: 'bg-cyan-500'
    },
  ];

  const stats = [
    { value: '25%', label: 'mais agendamentos confirmados via WhatsApp' },
    { value: '30%', label: 'mais visitas recorrentes com lembretes de fidelização' },
    { value: '25%', label: 'mais clientes aceitam serviços extras com ofertas automatizadas' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-28 pb-16">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Soluções para cada indústria
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Mais de 18.000 empresas usam ChatAlfa para automatizar vendas e atendimento. 
            Veja como podemos ajudar seu negócio.
          </p>
          <Button 
            onClick={login}
            size="lg" 
            className="bg-[#3b82f6] hover:bg-[#2563eb] text-lg px-8"
            data-testid="usecases-cta"
          >
            Começar gratuitamente
          </Button>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] py-12 mb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 text-white text-center">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <p className="text-4xl font-bold mb-2">{stat.value}</p>
                  <p className="text-blue-100">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Industries Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Escolha sua indústria</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry) => (
              <Card 
                key={industry.name} 
                className="hover:shadow-xl transition-all cursor-pointer group hover:border-[#3b82f6]"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 ${industry.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <industry.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{industry.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{industry.description}</p>
                  <ul className="space-y-2 mb-4">
                    {industry.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-[#3b82f6] rounded-full"></div>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-between text-[#3b82f6] hover:bg-blue-50 group-hover:bg-blue-50"
                    onClick={login}
                  >
                    Ver solução
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Typical Cases */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
              Casos de uso mais populares
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Automatize processos comuns em qualquer negócio
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Geração de Leads', desc: 'Capture contatos qualificados automaticamente' },
                { title: 'Qualificação', desc: 'Filtre prospects antes de passar para vendas' },
                { title: 'Atendimento', desc: 'Responda perguntas frequentes 24/7' },
                { title: 'Agendamentos', desc: 'Marque reuniões e consultas sem intervenção' },
                { title: 'Vendas', desc: 'Apresente produtos e processe pagamentos' },
                { title: 'Follow-up', desc: 'Reengaje leads e clientes automaticamente' },
                { title: 'Feedback', desc: 'Colete avaliações e NPS' },
                { title: 'Suporte', desc: 'Resolva problemas e escale quando necessário' },
              ].map((item) => (
                <Card key={item.title} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              O que nossos clientes dizem
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  quote: 'Centraliza as conversas com clientes e mantém um histórico completo das interações. Ajudou muito a equipe de suporte.',
                  author: 'Maria Silva',
                  role: 'Dona de Clínica Estética'
                },
                {
                  quote: 'O bot consegue gerenciar até mensagens de áudio longas, obtendo as informações necessárias automaticamente.',
                  author: 'João Santos',
                  role: 'Corretor de Imóveis'
                },
                {
                  quote: 'A capacidade de filtrar prospects reduziu a necessidade de follow-ups manuais, economizando muito tempo.',
                  author: 'Ana Costa',
                  role: 'Gerente de Vendas'
                },
              ].map((testimonial) => (
                <Card key={testimonial.author} className="bg-gray-50">
                  <CardContent className="p-6">
                    <p className="text-gray-600 mb-4 italic">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.author}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] text-white">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">
                  Não encontrou sua indústria?
                </h2>
                <p className="text-blue-100 mb-6">
                  ChatAlfa funciona para qualquer negócio que usa WhatsApp para se comunicar com clientes.
                </p>
                <Button 
                  onClick={login}
                  variant="secondary" 
                  size="lg" 
                  className="bg-white text-[#3b82f6] hover:bg-gray-100"
                >
                  Falar com especialista
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UseCasesPage;
